import { useBrand } from 'app/modules/brand-management/context/BrandContext'
import { FC, ImgHTMLAttributes } from 'react'
import { Link } from 'react-router-dom'
import { systemMode, useThemeMode } from '_metronic/partials'

interface LogoComponentProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** Force a specific logo variant, overriding theme detection */
  variant?: 'light' | 'dark'
  /** Size in pixels (sets both width and height) */
  size?: number | string
  /** Link destination (if provided, wraps logo in Link) */
  to?: string
  /** CSS class name */
  className?: string
  src?: string
}

const DEFAULT_LOGO_BY_MODE: Record<'light' | 'dark', string> = {
  light: '/media/logos/logo-white.png',
  dark: '/media/logos/logo-dark.png',
}

/**
 * LogoComponent
 * Centralized logo rendering for consistent branding across the app
 * Uses the current brand from BrandContext
 *
 * Usage:
 * <LogoComponent variant="dark" size={40} />
 * <LogoComponent to="/" variant="light" className="h-25px" />
 */
export const LogoComponent: FC<LogoComponentProps> = ({
  variant,
  size,
  to,
  className = 'h-30px h-lg-30px',
  alt,
  src,
  style,
  ...imgProps
}) => {
  const { currentBrand } = useBrand()
  const { mode } = useThemeMode()
  const resolvedMode = mode === 'system' ? systemMode : mode

  const logoSrc = src || currentBrand?.logo?.url || DEFAULT_LOGO_BY_MODE[variant || resolvedMode]
  const altText = alt || `${currentBrand?.fantasia || 'Lauduzone'} logo`

  const img = (
    <img
      src={logoSrc}
      alt={altText}
      className={className}
      style={{ maxWidth: '100%', objectFit: 'contain', ...style }}
      {...(size && { width: size, height: size })}
      {...imgProps}
    />
  )

  if (to) {
    return <Link to={to}>{img}</Link>
  }

  return img
}
